import { useMemo, useState } from 'react'
import {
  FaArrowLeft,
  FaBullhorn,
  FaChartLine,
  FaDollarSign,
  FaImage,
  FaMapMarkerAlt,
  FaRoute,
  FaSatelliteDish,
  FaShieldAlt,
  FaSignOutAlt,
  FaUser,
  FaUsers
} from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { useAuth } from 'contexts/auth.context'
import { useUser } from 'contexts/user.context'

import api from 'services/api'

import * as S from '../style'
import * as M from './style'

type ModerationItem = {
  id: string
  userId?: string | null
  contentType: string
  purpose: string
  decision: 'approved' | 'review' | 'blocked'
  reviewStatus: 'none' | 'pending' | 'approved' | 'rejected' | 'blocked'
  entityType?: string | null
  entityId?: string | null
  fieldName?: string | null
  textExcerpt?: string | null
  assetUrl?: string | null
  fileName?: string | null
  mimeType?: string | null
  provider?: string | null
  model?: string | null
  reason?: string | null
  categories: Record<string, boolean>
  scores: Record<string, number>
  reviewNote?: string | null
  reviewedAt?: string | null
  createdAt: string
  firstname?: string | null
  lastname?: string | null
  email?: string | null
  context?: {
    profileUserId?: string | null
    profilePath?: string | null
    itineraryId?: string | null
    itineraryTitle?: string | null
    itineraryAuthorId?: string | null
    itineraryAuthorName?: string | null
    itineraryPath?: string | null
    cityName?: string | null
    dayNumber?: number | null
    commentId?: string | null
    parentCommentId?: string | null
    parentCommentText?: string | null
    parentCommentAuthorName?: string | null
  }
}

type ModerationResponse = {
  items: ModerationItem[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  summary: {
    pending: number
    approved: number
    rejected: number
    blocked: number
  }
}

const purposeLabel = (purpose: string) => {
  const labels: Record<string, string> = {
    profile_picture: 'Foto de perfil',
    profile_cover: 'Capa do perfil',
    itinerary_cover: 'Capa do roteiro',
    itinerary_photo: 'Foto do roteiro',
    generic_image: 'Imagem',
    itinerary_comment: 'Comentário de roteiro',
    itinerary_comment_report: 'Denúncia de comentário'
  }
  return labels[purpose] || purpose
}

const statusLabel = (status: string) => {
  const labels: Record<string, string> = {
    pending: 'Aguardando análise',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
    blocked: 'Bloqueado automaticamente',
    none: 'Aprovado automaticamente'
  }
  return labels[status] || status
}

const AdminModeration = () => {
  const { user } = useUser()
  const { signOut } = useAuth()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState('pending')

  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-moderation', status],
    queryFn: async () => {
      const response = await api.get<ModerationResponse>(
        `/admin/moderation?status=${status}&contentType=all&limit=50`
      )
      return response.data
    },
    staleTime: 10 * 1000
  })

  const reviewMutation = useMutation({
    mutationFn: async ({
      id,
      action
    }: {
      id: string
      action: 'approve' | 'reject'
    }) => api.patch(`/admin/moderation/${id}`, { action }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-moderation'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-itineraries'] }),
        queryClient.invalidateQueries({ queryKey: ['userData'] })
      ])

      toast.success(
        variables.action === 'approve'
          ? 'Conteúdo aprovado.'
          : 'Conteúdo rejeitado e removido do uso público atual.',
        { theme: 'dark' }
      )
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          'Não foi possível concluir a revisão.',
        { theme: 'dark' }
      )
    }
  })

  const topScores = (item: ModerationItem) =>
    Object.entries(item.scores || {})
      .filter(([, value]) => Number(value) >= 0.1)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .slice(0, 5)

  const summary = useMemo(
    () =>
      data?.summary || {
        pending: 0,
        approved: 0,
        rejected: 0,
        blocked: 0
      },
    [data?.summary]
  )

  return (
    <S.Page>
      <S.Sidebar>
        <S.BrandBlock>
          <S.BrandLink to="/">
            <S.Logo src="/images/header-logo.png" alt="RedSterna" />
          </S.BrandLink>
          <S.AdminBadge>
            <FaShieldAlt />
            Admin
          </S.AdminBadge>
        </S.BrandBlock>

        <S.SidebarBody>
          <S.SidebarLabel>Gestão</S.SidebarLabel>
          <S.SidebarNav>
            <S.SidebarNavLink to="/admin">
              <FaChartLine /> Dashboard
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/usuarios">
              <FaUsers /> Usuários
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/localidades">
              <FaMapMarkerAlt /> Localidades
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/roteiros">
              <FaRoute /> Roteiros
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/moderacao" $active>
              <FaImage /> Moderação
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/anuncios">
              <FaBullhorn /> Anúncios
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/radar">
              <FaSatelliteDish /> Radar
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/comissoes">
              <FaDollarSign /> Comissões
            </S.SidebarNavLink>
          </S.SidebarNav>
        </S.SidebarBody>

        <S.SidebarFooter>
          <S.SidebarLink to="/minha-conta">
            <FaUser /> Meu perfil
          </S.SidebarLink>
          <S.SidebarLink to="/">
            <FaArrowLeft /> Voltar ao site
          </S.SidebarLink>
        </S.SidebarFooter>
      </S.Sidebar>

      <S.Main>
        <S.Topbar>
          <div>
            <S.TopbarEyebrow>RedSterna Admin</S.TopbarEyebrow>
            <S.TopbarTitle>Moderação de conteúdo</S.TopbarTitle>
          </div>

          <S.UserArea>
            <S.UserAvatar>
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={fullName || 'Administrador'}
                />
              ) : (
                <FaUser />
              )}
            </S.UserAvatar>
            <S.UserInfo>
              <strong>{fullName || 'Administrador'}</strong>
              <span>{user?.email}</span>
            </S.UserInfo>
            <S.AdminChip>Admin</S.AdminChip>
            <S.SignOutButton type="button" onClick={signOut} aria-label="Sair">
              <FaSignOutAlt />
            </S.SignOutButton>
          </S.UserArea>
        </S.Topbar>

        <S.Content>
          <S.DashboardHeader>
            <div>
              <h1>Fila de moderação</h1>
              <p>
                Conteúdos ambíguos continuam disponíveis, mas aparecem aqui para
                revisão humana. Conteúdo explícito é bloqueado antes da
                publicação.
              </p>
            </div>
          </S.DashboardHeader>

          <M.Summary>
            <M.SummaryCard>
              <strong>{summary.pending}</strong>
              <span>Pendentes</span>
            </M.SummaryCard>
            <M.SummaryCard>
              <strong>{summary.approved}</strong>
              <span>Aprovados manualmente</span>
            </M.SummaryCard>
            <M.SummaryCard>
              <strong>{summary.rejected}</strong>
              <span>Rejeitados</span>
            </M.SummaryCard>
            <M.SummaryCard>
              <strong>{summary.blocked}</strong>
              <span>Bloqueados automaticamente</span>
            </M.SummaryCard>
          </M.Summary>

          <M.Toolbar>
            <M.Select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="pending">Aguardando análise</option>
              <option value="approved">Aprovados</option>
              <option value="rejected">Rejeitados</option>
              <option value="blocked">Bloqueados automaticamente</option>
              <option value="all">Todos</option>
            </M.Select>
          </M.Toolbar>

          {isLoading && (
            <S.StateCard>Carregando fila de moderação...</S.StateCard>
          )}
          {isError && (
            <S.StateCard $error>
              Não foi possível carregar a fila de moderação.
            </S.StateCard>
          )}

          {data && data.items.length === 0 && (
            <S.StateCard>Nenhum conteúdo neste filtro.</S.StateCard>
          )}

          {data && data.items.length > 0 && (
            <M.Grid>
              {data.items.map((item) => {
                const userName = [item.firstname, item.lastname]
                  .filter(Boolean)
                  .join(' ')

                return (
                  <M.Card key={item.id}>
                    <M.Preview>
                      {item.contentType === 'text' ? (
                        <M.TextPreview>
                          {item.textExcerpt || 'Texto sem prévia disponível.'}
                        </M.TextPreview>
                      ) : item.assetUrl ? (
                        <img src={item.assetUrl} alt="Conteúdo em moderação" />
                      ) : (
                        <span>Imagem bloqueada antes da publicação</span>
                      )}
                    </M.Preview>
                    <M.Body>
                      <M.Meta>
                        <strong>
                          {item.contentType === 'text'
                            ? item.entityType === 'itinerary_comment'
                              ? purposeLabel(item.purpose)
                              : 'Texto do roteiro'
                            : purposeLabel(item.purpose)}
                        </strong>
                        <M.Status $kind={item.reviewStatus}>
                          {statusLabel(item.reviewStatus)}
                        </M.Status>
                        <span>
                          {userName || 'Usuário'}{' '}
                          {item.email ? `· ${item.email}` : ''}
                        </span>
                        {item.contentType === 'image' && (
                          <span>{item.fileName || 'Arquivo sem nome'}</span>
                        )}
                        {item.contentType === 'text' && item.entityId && (
                          <span>
                            {item.entityType === 'itinerary_comment'
                              ? 'Comentário'
                              : 'Roteiro'}
                            : {item.entityId}
                          </span>
                        )}
                        {item.reason && (
                          <span>Motivo automático: {item.reason}</span>
                        )}
                      </M.Meta>

                      {(item.context?.itineraryId ||
                        item.context?.profileUserId ||
                        item.context?.parentCommentText) && (
                        <M.ContextBox>
                          <strong>Contexto</strong>

                          {item.context?.itineraryId && (
                            <M.ContextGrid>
                              <span>
                                <b>Roteiro:</b>{' '}
                                {item.context.itineraryTitle ||
                                  item.context.itineraryId}
                              </span>
                              {item.context.itineraryAuthorName && (
                                <span>
                                  <b>Autor do roteiro:</b>{' '}
                                  {item.context.itineraryAuthorName}
                                </span>
                              )}
                              {item.context.dayNumber && (
                                <span>
                                  <b>Dia:</b> {item.context.dayNumber}
                                </span>
                              )}
                              {item.context.cityName && (
                                <span>
                                  <b>Cidade:</b> {item.context.cityName}
                                </span>
                              )}
                            </M.ContextGrid>
                          )}

                          {item.context?.parentCommentText && (
                            <M.ParentContext>
                              <small>
                                {item.context.parentCommentAuthorName
                                  ? `Resposta a ${item.context.parentCommentAuthorName}`
                                  : 'Comentário ao qual esta resposta foi enviada'}
                              </small>
                              <p>{item.context.parentCommentText}</p>
                            </M.ParentContext>
                          )}

                          <M.ContextActions>
                            {item.context?.itineraryPath && (
                              <a
                                href={item.context.itineraryPath}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Ver no roteiro
                              </a>
                            )}
                            {item.context?.profilePath && (
                              <a
                                href={item.context.profilePath}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Ver perfil
                              </a>
                            )}
                          </M.ContextActions>
                        </M.ContextBox>
                      )}

                      <M.ScoreList>
                        {topScores(item).map(([category, score]) => (
                          <M.Score key={category}>
                            {category}: {Math.round(Number(score) * 100)}%
                          </M.Score>
                        ))}
                      </M.ScoreList>

                      {item.reviewStatus === 'pending' && (
                        <M.Actions>
                          <button
                            type="button"
                            disabled={reviewMutation.isPending}
                            onClick={() =>
                              reviewMutation.mutate({
                                id: item.id,
                                action: 'approve'
                              })
                            }
                          >
                            Manter
                          </button>
                          <button
                            type="button"
                            disabled={reviewMutation.isPending}
                            onClick={() =>
                              reviewMutation.mutate({
                                id: item.id,
                                action: 'reject'
                              })
                            }
                          >
                            Remover
                          </button>
                        </M.Actions>
                      )}
                    </M.Body>
                  </M.Card>
                )
              })}
            </M.Grid>
          )}
        </S.Content>
      </S.Main>
    </S.Page>
  )
}

export default AdminModeration
